import { motion } from 'framer-motion';
import { ArrowRight, Clock, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import heroCnc from '@/assets/hero-cnc.webp';
import tuvLogo from '@/assets/tuv-iso-9001.webp';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/lib/i18n';

export function HeroSection() {
  const t = useT();
  const stats = [
    { icon: Clock, value: '30+', label: t('Jahre Erfahrung', 'Years of experience') },
    { icon: Award, value: '100%', label: t('Präzision', 'Precision') },
  ];
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={heroCnc}
          alt={t(
            'CNC Präzisionsbearbeitung bei FERNAU – hochpräzise Dreh- und Frästeile aus Darmstadt',
            'CNC precision machining at FERNAU – high-precision turned and milled parts from Darmstadt, Germany',
          )}
          className="w-full h-full object-cover image-protected"
          draggable={false}
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/80 to-background/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 grid-pattern opacity-30" />

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="badge-hero">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              {t('Zertifiziert. Transparent. Zuverlässig.', 'Certified. Transparent. Reliable.')}
            </span>
          </motion.div>

          <motion.h1
            className="mt-8 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="text-gradient-steel">{t('CNC-Präzision', 'CNC Precision')}</span>
            <br />
            <span className="text-primary">{t('auf höchstem Niveau', 'at the highest level')}</span>
          </motion.h1>

          <motion.p
            className="hero-description mt-6 text-lg text-muted-foreground max-w-lg leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t(
              'CNC-Drehen, Fräsen und komplexe Baugruppen – von der Konstruktion bis zur Auslieferung. Ihr Partner für Maschinenbau, Messtechnik und Medizintechnik.',
              'CNC turning, milling and complex assemblies — from design to delivery. Your partner for mechanical engineering, metrology and medical technology.',
            )}
          </motion.p>

          <motion.div
            className="mt-8 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Button variant="hero" size="xl" asChild>
              <LocalizedLink to="/kontakt">
                {t('Projekt anfragen', 'Request a project')}
                <ArrowRight className="w-5 h-5" />
              </LocalizedLink>
            </Button>
            <Button variant="heroOutline" size="xl" asChild>
              <LocalizedLink to="/leistungen">{t('Unsere Leistungen', 'Our services')}</LocalizedLink>
            </Button>
          </motion.div>

          <motion.div
            className="mt-12 flex flex-wrap items-center gap-6 sm:gap-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {/* TÜV ISO 9001 Badge */}
            <a
              href="http://www.tuvsud.com/ms-zert"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('TÜV SÜD ISO 9001 Zertifizierung', 'TÜV SÜD ISO 9001 Certification')}
              className="shrink-0 transition-transform hover:scale-105"
            >
              <img
                src={tuvLogo}
                alt={t(
                  'TÜV SÜD ISO 9001 – Zertifiziertes Qualitätsmanagementsystem',
                  'TÜV SÜD ISO 9001 – Certified quality management system',
                )}
                className="h-16 sm:h-20 w-auto"
                width={160}
                height={80}
                draggable={false}
              />
            </a>

            {stats.map((stat) => (
              <div key={stat.label} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-secondary/80 border border-border/30 flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold font-mono">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
    </section>
  );
}
